import React, { useState } from "react";
import toast from "react-hot-toast";
import {
    Box, Button, Flex, Icon, SimpleGrid, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdBolt } from "react-icons/md";
import RateField from "./RateField";
import { SEED_DEFAULTS, SEED_PAIRS } from "./currencyRatesConstants";
import { buildSeedPayloads, seedPairs } from "./seedPairs";

/**
 * Renders one line of the outcome report.
 * @param {Object} props - label, the direction list and its color
 * @returns {React.ReactElement|null}
 */
function ReportLine({ label, items, color }) {
    if (!items?.length) return null;
    return (
        <Text fontSize="xs" color={color} mt="4px">
            {label} : {items.join(", ")}
        </Text>
    );
}

/**
 * Creates the missing directions and reports what happened.
 * @param {Object} args - form state and the surrounding setters
 */
async function runSeed({ form, setLoading, setReport, onSeeded }) {
    const payloads = buildSeedPayloads(form);
    if (!payloads.length) {
        toast.error("Renseigne au moins un taux valide");
        return;
    }
    setLoading(true);
    try {
        const result = await seedPairs(payloads);
        setReport(result);
        if (result.failed.length) toast.error(`${result.failed.length} direction(s) en échec`);
        else if (result.created.length) toast.success(`${result.created.length} direction(s) créée(s)`);
        else toast.success("Toutes les directions étaient déjà configurées");
        onSeeded?.();
    } finally {
        setLoading(false);
    }
}

/**
 * One-click replacement for scripts/seed_currency_rates.py: creates the four
 * opening directions (EUR/MGA and USD/MGA, both ways) without an SSH session.
 * A direction already configured is left untouched, so the button is safe to
 * press twice.
 * @param {Object} props - onSeeded callback refreshing the table
 * @returns {React.ReactElement}
 */
function QuickSeedPanel({ onSeeded }) {
    const [form, setForm] = useState(SEED_DEFAULTS);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const cardBg = useColorModeValue("white", "navy.800");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

    return (
        <Box bg={cardBg} borderRadius="16px" p="20px" mb="20px">
            <Flex align="center" gap="10px" mb="6px">
                <Icon as={MdBolt} color="brand.500" w="20px" h="20px" />
                <Text fontSize="md" fontWeight="700" color={titleColor}>Initialisation rapide</Text>
            </Flex>
            <Text fontSize="sm" color={subColor} mb="16px">
                Crée les quatre directions d'ouverture — EUR ↔ MGA et USD ↔ MGA — à partir des deux
                taux directs. Le sens inverse est calculé automatiquement, puis reste modifiable
                indépendamment. Une direction déjà configurée n'est jamais écrasée.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px" mb="16px">
                {SEED_PAIRS.map((pair) => (
                    <RateField
                        key={pair.field}
                        label={pair.label}
                        value={form[pair.field]}
                        onChange={setField(pair.field)}
                        placeholder="5000"
                        helper={`Combien d'ariary vaut 1 ${pair.base}`}
                    />
                ))}
                <RateField
                    label="Marge (%)"
                    value={form.margin}
                    onChange={setField("margin")}
                    placeholder="3"
                    helper="Spread ajouté au montant du payeur"
                />
            </SimpleGrid>

            <Button
                size="sm"
                colorScheme="brand"
                isLoading={loading}
                onClick={() => runSeed({ form, setLoading, setReport, onSeeded })}
            >
                Créer les directions manquantes
            </Button>

            {report && (
                <Box mt="12px">
                    <ReportLine label="Créées" items={report.created} color="green.400" />
                    <ReportLine label="Déjà configurées" items={report.skipped} color={subColor} />
                    <ReportLine label="Échecs" items={report.failed} color="red.400" />
                </Box>
            )}
        </Box>
    );
}

export default QuickSeedPanel;
